import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Waves, Map, Shield, Clock, Info } from "lucide-react";
import { toast } from "sonner";

interface StressToolsProps {
  user: any;
}

export const StressTools = ({ user }: StressToolsProps) => {
  const [activeTab, setActiveTab] = useState('mapping');

  const tools = [
    {
      id: 'mapping',
      name: 'Stress Mapping',
      icon: Map,
      description: 'Identify your stress sources, triggers, and patterns',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      why: 'Stress often feels like a vague, overwhelming cloud. Mapping makes it concrete and manageable by identifying specific stressors, their intensity, and what you can/can\'t control. This clarity is the first step to taking action rather than being passively overwhelmed.',
      how: 'Draw three circles: 1) Things you can control (your reactions, boundaries, choices), 2) Things you can influence (requesting help, expressing needs), 3) Things outside your control (others\' behaviour, past events). Place each stressor in the appropriate circle. Focus your energy on circles 1 and 2.',
      science: 'Stress occurs when perceived demands exceed perceived resources. Mapping helps you accurately assess demands (often lower than they feel) and resources (often higher than you realise). It activates problem-solving brain regions, moving you out of pure stress response.'
    },
    {
      id: 'boundaries',
      name: 'Boundary Setting',
      icon: Shield,
      description: 'Protect your time, energy, and well-being with clear limits',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      why: 'Many people experience stress because they haven\'t set or maintained boundaries-saying yes when they want to say no, overextending, or allowing others to make demands they can\'t meet. Boundaries aren\'t selfish; they\'re essential for sustainable functioning and prevent burnout.',
      how: 'Identify one area where you need a boundary (time, emotional energy, work demands). Use this format: "I can/will [what you can do], but I can\'t/won\'t [what you can\'t do]." Example: "I can help with this project until 5pm, but I won\'t work evenings this week." Practice with low-stakes situations first.',
      science: 'Chronic stress occurs when we repeatedly engage in activities that drain us without adequate recovery. Boundaries protect your nervous system from constant activation. Research shows people who maintain healthy boundaries have lower cortisol levels and better work-life balance.'
    },
    {
      id: 'time-management',
      name: 'Time Management',
      icon: Clock,
      description: 'Prioritise effectively and reduce overwhelm from competing demands',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      why: 'Feeling "not enough time" creates constant stress. But often the issue isn\'t time scarcity-it\'s trying to do too much, poor prioritization, or not protecting time for essential activities (rest, relationships, self-care). Time management is really priority management.',
      how: 'Use the Eisenhower Matrix: 1) Urgent & Important (do first), 2) Important but Not Urgent (schedule these-prevention, planning, relationships), 3) Urgent but Not Important (delegate/minimise), 4) Neither (eliminate). Most stress comes from living in quadrants 1 and 3 while neglecting quadrant 2.',
      science: 'Time pressure activates the same stress response as physical threats. Effective time management reduces this activation. Studies show that scheduling important-but-not-urgent activities (exercise, relationships, rest) significantly reduces stress hormones and increases life satisfaction.'
    },
    {
      id: 'selfcare',
      name: 'Self-Care Planning',
      icon: Waves,
      description: 'Build sustainable practices for nervous system regulation',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      why: 'Self-care isn\'t selfish or indulgent-it\'s maintaining the basic conditions your nervous system needs to function. Without it, you\'re running on empty, making everything harder and stress more intense. Self-care is preventative medicine against burnout.',
      how: 'Create a self-care menu across categories: Physical (sleep, movement, nutrition), Emotional (therapy, journaling, talking to friends), Mental (breaks, learning, creativity), Spiritual (values, meaning, connection). Schedule at least one from each category weekly. Treat these as non-negotiable appointments.',
      science: 'Chronic stress without recovery leads to allostatic load-wear and tear on your body from sustained stress response. Self-care activities activate the parasympathetic nervous system (rest and digest), providing essential recovery periods that prevent stress from becoming chronic and damaging.'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-br from-background to-secondary/5 border-secondary/20">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-lg bg-secondary/10">
              <Waves className="h-6 w-6 text-secondary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Stress Management Tools</CardTitle>
              <CardDescription>Practical strategies for work stress, life transitions, and burnout prevention</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-background/50 backdrop-blur-sm rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium flex items-center gap-2">
              <Info className="h-4 w-4 text-secondary" />
              Understanding Stress
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Stress isn\'t inherently bad-it\'s your body mobilizing resources to meet demands. Problems arise when stress 
              is chronic (no recovery), overwhelming (demands exceed resources), or unnecessary (worrying about things you 
              can\'t control). These tools help you respond to stress more effectively and build resilience.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tools Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex overflow-x-auto scrollbar-hide w-full gap-1 bg-gradient-calm border border-border/50 p-1 justify-start sm:justify-center">
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <TabsTrigger 
                key={tool.id} 
                value={tool.id}
                className="gap-1.5 shrink-0 px-3 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">{tool.name.split(' ')[0]}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {tools.map((tool) => {
          const Icon = tool.icon;
          return (
            <TabsContent key={tool.id} value={tool.id} className="mt-6 space-y-6">
              <Card className="border-border/50">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${tool.bgColor}`}>
                      <Icon className={`h-5 w-5 ${tool.color}`} />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{tool.name}</CardTitle>
                      <CardDescription>{tool.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Why This Matters */}
                  <div className="bg-gradient-to-br from-secondary/20 to-primary/15 rounded-lg p-4 space-y-2">
                    <p className="text-base font-medium text-foreground font-aileron">💡 Why This Matters</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {tool.why}
                    </p>
                  </div>

                  {/* How to Use It */}
                  <div className="bg-gradient-to-br from-primary/20 to-secondary/15 rounded-lg p-4 space-y-2">
                    <p className="text-base font-medium text-foreground font-aileron">🔧 How to Use This Tool</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {tool.how}
                    </p>
                  </div>

                  {/* The Science */}
                  <div className="bg-gradient-to-br from-secondary/20 to-primary/15 rounded-lg p-4 space-y-2">
                    <p className="text-base font-medium text-foreground font-aileron">🧠 The Science</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {tool.science}
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      className="flex-1 text-base font-aileron" 
                      onClick={() => toast.info(`${tool.name} interactive exercise is being developed. Check back soon!`)}
                    >
                      Start Exercise
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1 text-base font-aileron" 
                      onClick={() => toast.info(`${tool.name} worksheet coming soon!`)}
                    >
                      View Worksheet
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Stress vs Burnout */}
      <Card className="border-secondary/50 bg-secondary/5">
        <CardContent className="pt-6">
          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">🔥 Know the Difference: Stress vs. Burnout</p>
            <div className="grid md:grid-cols-2 gap-4 text-xs">
              <div className="space-y-2">
                <p className="font-medium text-foreground">Stress Symptoms:</p>
                <ul className="list-disc list-inside text-foreground space-y-1">
                  <li>Feeling overwhelmed with too much to do</li>
                  <li>Physical tension and hyperactivity</li>
                  <li>Anxiety and worry</li>
                  <li>Short-term, recovers with rest</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-foreground">Burnout Symptoms:</p>
                <ul className="list-disc list-inside text-foreground space-y-1">
                  <li>Feeling empty, helpless, detached</li>
                  <li>Physical exhaustion and numbness</li>
                  <li>Loss of motivation and hopelessness</li>
                  <li>Long-term, requires significant intervention</li>
                </ul>
              </div>
            </div>
            <p className="text-xs text-foreground">
              <strong>If you\'re experiencing burnout</strong> (not just stress), these tools are a starting point, 
              but you likely need more support-professional help, time off, major life changes. Burnout can\'t be fixed 
              with self-care alone.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};